import { ChangeDetectionStrategy, ChangeDetectorRef, Component, OnDestroy, ViewChild, TemplateRef, Optional } from '@angular/core';
import { Subject, finalize, takeUntil } from 'rxjs';
import { ModalRef, ModalService, SubModalRef, Lesson, OrderSecurity, TRANSFERS_TABS, TransfersTab, TransfersHelpArticleId } from '@tdwb/core';

// eslint-disable-next-line td-wb-rules/cross-lazyload-module-import-rule
import { TradingHelpContextService } from '@tdwb/lazy/trading';

import { LearnRestService } from '../../learn-rest.service';
import { DisplayLearnInformation, LearnLinkIdMapping, TradingLessonsData } from '../../domain';

@Component({
  selector: 'td-wb-trading-learn-modal',
  templateUrl: './trading-learn-modal.component.html',
  styleUrls: ['./trading-learn-modal.component.scss'],
  exportAs: 'tdWbContextualLearning',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class TradingLearnModalComponent implements OnDestroy {
  @ViewChild('contextualLearningTemplate', { static: true }) readonly contextualLearning: TemplateRef<unknown>;
  tabId: OrderSecurity | TransfersTab;
  page: string;
  modalTitle: string;
  modalIcon: string;
  modalArticleId: string;
  contextualLesson: Lesson;
  relatedLessons: Array<Lesson>;
  loadingIndicator: boolean = true;
  showSubHeader: boolean = true;

  private modalReference: ModalRef<unknown>;
  private subModalRef: SubModalRef;
  private readonly destroy: Subject<void> = new Subject<void>();

  constructor(private learnRestService: LearnRestService,
              private changeDetectorRef: ChangeDetectorRef,
              @Optional() private modalRef: ModalRef<unknown>,
              private modalService: ModalService) {
  }

  openContextualLearningModal(tabId: OrderSecurity | TransfersTab, page: DisplayLearnInformation): void {
    this.tabId = tabId;
    this.getModalInformation(page);
    this.learnRestService.fetchTradingLessons(LearnLinkIdMapping[tabId])
      .pipe(
        takeUntil(this.destroy),
        finalize(() => this.changeDetectorRef.markForCheck())
      )
      .subscribe({
        next: (data: TradingLessonsData): void => {
          this.contextualLesson = data.contextualLesson;
          this.relatedLessons = data.relatedLessons;
          this.loadingIndicator = false;
          if (this.modalRef === undefined || this.modalRef === null) {
            this.modalReference = this.modalService.open(this.contextualLearning, {
              size: 'medium'
            });
          } else {
            this.subModalRef = this.modalRef.openSubModal(this.contextualLearning);
          }
        },
        error: (): void => {
          this.loadingIndicator = false;
          this.relatedLessons = [];
        }
      });
  }

  closeModal(): void {
    this.modalReference?.close();
    this.subModalRef?.close();
  }

  selectLesson(lesson: Lesson): void {
    this.contextualLesson = lesson;
    this.changeDetectorRef.markForCheck();
  }

  private getArticleIdCreate(): string {
    return TradingHelpContextService.getCreateArticleId(this.tabId ?? '', 'edit');
  }

  getBackButtonForOrigin(): string {
    return `tdWb.tradingLearn.information.${this.tabId}.BACK`;
  }

  getBackLinkForOrigin(): string {
    if (TRANSFERS_TABS.includes(this.tabId as TransfersTab)) {
      return `tdWb.tradingLearn.information.${this.tabId}.BACK_LINK`;
    } else {
      return `tdWb.tradingLearn.information.${this.tabId}.BACK`;
    }
  }

  getModalInformation(page: DisplayLearnInformation): void {
    switch(page) {
      case 'transfers':
        this.modalTitle =  'TRANSFERS_TITLE';
        this.modalIcon = 'tertiary--arrow-leftright';
        if (this.tabId === 'cash') {
          this.modalArticleId = TransfersHelpArticleId.TRAN_ENTER;
        } else if (this.tabId === 'ofi') {
          this.modalArticleId = TransfersHelpArticleId.OFI_TRANSFERS;
        } else {
          this.modalArticleId = TransfersHelpArticleId.SECURITY_TRANSFERS;
        }
        break;
      case 'holdings':
        this.modalTitle =  'HEADER';
        this.modalIcon = undefined;
        this.modalArticleId = 'acct_access';
        this.showSubHeader = false;
        break;
      default:
        this.modalTitle =  'BUYSELL_TITLE';
        this.modalIcon = 'tertiary--arrow-updown';
        this.modalArticleId = this.getArticleIdCreate();
        break;
    }
  }

  ngOnDestroy(): void {
    this.destroy.next();
    this.destroy.complete();
  }

}
